import { X as CloseIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { clsx } from '../../utils/clsx';
import Skeleton from '../Skeleton/Skeleton';
import Button from '../Button/Button';
import styles from './LinkPreviewCard.module.css';

const PLACEHOLDER_ICON = '🔗';

const LinkPreviewCard = ({
  siteName,
  title,
  description,
  imageUrl,
  url,
  loading = false,
  error = false,
  onRemove,
  className,
  size = 'default',
}) => {
  const displayUrl = useMemo(() => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace(/^www\./i, '') + parsed.pathname;
    } catch (err) {
      return url;
    }
  }, [url]);

  return (
    <article
      className={clsx(
        styles.card,
        styles[`cardSize-${size}`],
        loading && styles.loading,
        error && styles.error,
        className,
      )}
    >
      <div className={styles.thumbnail} aria-hidden={loading || error ? 'true' : 'false'}>
        {loading ? (
          <Skeleton className={styles.thumbnailSkeleton} />
        ) : imageUrl ? (
          <img src={imageUrl} alt="" />
        ) : (
          <span className={styles.thumbnailPlaceholder}>{PLACEHOLDER_ICON}</span>
        )}
      </div>

      <div className={styles.body}>
        {loading ? (
          <>
            <Skeleton width="35%" height={14} className={styles.line} />
            <Skeleton width="80%" height={18} className={styles.line} />
            <Skeleton width="95%" height={14} className={styles.line} />
            <Skeleton width="45%" height={14} className={styles.line} />
          </>
        ) : error ? (
          <div className={styles.errorCopy}>
            Something went wrong loading this preview.
          </div>
        ) : (
          <>
            {siteName ? <div className={styles.site}>{siteName}</div> : null}
            {title ? <div className={styles.title}>{title}</div> : null}
            {description ? <div className={styles.description}>{description}</div> : null}
            {url ? (
              <a
                className={styles.link}
                href={url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {displayUrl}
              </a>
            ) : null}
          </>
        )}
      </div>

      {onRemove ? (
        <Button
          variant="ghost"
          size="xs"
          className={styles.closeButton}
          aria-label="Remove link preview"
          onClick={onRemove}
          icon={<CloseIcon size={16} aria-hidden />}
        />
      ) : null}
    </article>
  );
};

LinkPreviewCard.propTypes = {
  siteName: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  url: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRemove: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.oneOf(['default', 'compact']),
};

export default LinkPreviewCard;
